import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, TextField, Box, Stack, Typography, FormControlLabel, Switch } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { Edit } from "@mui/icons-material";
import { JobHttpService } from "../../service";
import Done from "core/components/shared/Done";
import Error from "core/components/shared/Error";
import Loading from "core/components/shared/Loading";
import ImageFieldWithGallery from "cms/components/widgets/ImageFieldWithGallery";

function CategoryForm({ id, onEditDone, ...props }) {
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            status: true // Default to active
        }
    });
    const [editingItem, setEditingItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [status, setStatus] = useState(true); // Default to active

    useEffect(() => {
        if (id) {
            setLoading(true);
            JobHttpService.loadCategory(id)
                .then(item => {
                    setEditingItem(item);
                    setImageUrl(item.image || '');
                    const itemStatus = item.status !== undefined ? item.status : true;
                    setStatus(itemStatus);
                    setValue('status', itemStatus);
                    for (const key in item) {
                        if (key !== 'status') {
                            setValue(key, item[key]);
                        }
                    }
                    setLoading(false);
                })
                .catch(err => {
                    setError("Failed to load category");
                    setLoading(false);
                });
        } else {
            setImageUrl('');
            setStatus(true); // Reset to active for new items
            setValue('status', true);
        }
    }, [id, setValue]);

    const saveChanges = (data) => {
        setError(null);
        setDone(false);
        setLoading(true);

        const body = {
            id: id || data.id,
            title: data.title,
            image: imageUrl,
            status: status !== undefined ? status : true // Use state value, default to true
        };

        JobHttpService.saveCategory(body)
            .then(response => {
                setLoading(false);
                setDone(true);
                setError(null);
                setTimeout(() => {
                    if (onEditDone) {
                        onEditDone(response);
                    }
                }, 500);
            })
            .catch(err => {
                setLoading(false);
                setError(err.message || "Something went wrong");
            });
    };

    return (
        <Box>
            <form onSubmit={handleSubmit(saveChanges)}>
                <Stack spacing={3}>
                    <TextField
                        fullWidth
                        label="Title"
                        value={watch('title') ?? ""}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.title}
                        helperText={errors.title?.message}
                        {...register("title", { required: "Title is required" })}
                    />

                    <ImageFieldWithGallery
                        label="Category Image"
                        value={imageUrl}
                        onChange={(url) => {
                            setImageUrl(url);
                            setValue('image', url);
                        }}
                        helperText="Select an image from the gallery or upload a new one"
                    />

                    {imageUrl && (
                        <Box>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                Preview:
                            </Typography>
                            <Box
                                component="img"
                                src={imageUrl}
                                alt="Category preview"
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: 200,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    objectFit: 'cover'
                                }}
                                onError={(e) => {
                                    e.target.src = '/assets/images/placeholder.png';
                                }}
                            />
                        </Box>
                    )}

                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={status}
                                    onChange={(e) => {
                                        const newStatus = e.target.checked;
                                        setStatus(newStatus);
                                        setValue('status', newStatus);
                                    }}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>
                                        Status
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {status ? 'Active' : 'Inactive'} - {status ? 'Category will be visible to users' : 'Category will be hidden'}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={editingItem ? <Edit /> : <AddIcon />}
                            disabled={loading}
                        >
                            {editingItem ? "Save Changes" : "Add Category"}
                        </Button>
                    </Box>

                    {done && <Done />}
                    {error && <Error message={error} />}
                    {loading && <Loading />}
                </Stack>
            </form>
        </Box>
    );
}

export default CategoryForm;

